import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import AceEditor from 'react-ace';

import 'ace-builds/src-noconflict/mode-json';
import { ACE_THEME } from '../../../lib/aceTheme';
import 'ace-builds/src-noconflict/ext-language_tools';

import Navbar from '../../../components/Navbar';
import PageWithAsyncData from '../../../components/PageWithAsyncData';
import { Input } from '../../../components/elements/Input';
import Tabs from '../../../components/elements/Tabs';
import Button from '../../../components/elements/Button';
import {
  StickyHeaderModal,
  ModalTitleContainer,
  ModalTitleMain,
  ModalTitleCaption,
} from '../../../components/Admin/AssessmenTest/common';
import { PSmall, LabelUppercase } from '../../../components/elements/Text';
import { PageTitle } from '../../../components/Admin/AdminLayout';
import { CmsProjectDefinitionSchema } from '../../../schemas/applied-project';
import type { CmsProjectDefinition } from '../../../schemas/applied-project';
import { z } from 'zod';
import { ROUTES } from '../../../routing';
import {
  useCmsGetProject,
  useCmsCreateProject,
  useCmsUpdateProject,
} from '../../../hooks/use-api/admin';
import CMSPageWrapper, {
  BottomBar,
  SmallButton,
} from '../../../components/Admin/CMSPageWrapper';
import { CMS_SIDEBAR_PAGES } from '../../../components/Admin/CMSSideBar';

const PREVIEW_STORAGE_KEY = 'enki:preview:project';

const JsonBodySchema = CmsProjectDefinitionSchema.omit({
  name: true,
  slug: true,
}).extend({
  name: z.string().optional(),
  slug: z.string().optional(),
});

type JsonBody = z.infer<typeof JsonBodySchema>;

function formatZodError(error: z.ZodError): string {
  return error.issues
    .slice(0, 5)
    .map((issue) => {
      const path = issue.path.length > 0 ? issue.path.join('.') : 'root';
      return `${path}: ${issue.message}`;
    })
    .join('\n');
}

type ValidationResult =
  | { valid: true; data: JsonBody; error: null }
  | { valid: false; data: null; error: string };

function validateJsonBody(json: string): ValidationResult {
  try {
    const parsed = JSON.parse(json);
    const result = JsonBodySchema.safeParse(parsed);
    if (!result.success) {
      return { valid: false, data: null, error: formatZodError(result.error) };
    }
    return { valid: true, data: result.data, error: null };
  } catch (e) {
    return {
      valid: false,
      data: null,
      error: `Invalid JSON: ${(e as Error).message}`,
    };
  }
}

function projectToEditorJson({
  name,
  slug,
  publishStatus,
  isProsumer,
  ...rest
}: CmsProjectDefinition): string {
  void name;
  void slug;
  void publishStatus;
  void isProsumer;
  return JSON.stringify(rest, null, 2);
}

const EditorPageLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 16px;
  padding-top: 16px;
  padding-bottom: 16px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-shrink: 0;

  @media screen and (max-width: 600px) {
    flex-direction: column;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const Label = styled(LabelUppercase)`
  margin-bottom: 8px;
  flex-shrink: 0;
  margin-top: 0;
`;

// enki addition (not in the source editor): pasting a definition also parks a
// copy in localStorage for the preview tab, so the editor needs a way to drop
// both at once rather than leaving a stale payload behind.
const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-shrink: 0;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-bottom: 8px;
  font-family: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textDanger};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const EditorWrapper = styled.div<{ hasError?: boolean }>`
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.colors.textDanger : theme.colors.bgActive};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgDefault};
  transition: border-color 200ms ease-in-out;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ErrorText = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textDanger};
  margin: 8px 0 0;
  white-space: pre-line;
`;

const StatusToggleWrapper = styled.div`
  flex-shrink: 0;
  width: 200px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const ModalBody = styled.p`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`;

export default function ProjectEditor() {
  const { slug } = useParams<{ slug: string }>();
  const isEditMode = !!slug;
  const history = useHistory();

  const {
    data: existingProject,
    isLoading,
    error,
    refetch,
  } = useCmsGetProject(slug ?? '');
  const createMutation = useCmsCreateProject();
  const updateMutation = useCmsUpdateProject(slug ?? '');

  const [name, setName] = React.useState('');
  const [projectSlug, setProjectSlug] = React.useState('');
  const [projectJson, setProjectJson] = React.useState('{}');
  const [jsonError, setJsonError] = React.useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = React.useState(false);
  const [publishStatus, setPublishStatus] =
    React.useState<'LIVE' | 'ARCHIVED'>('ARCHIVED');
  const [isProsumer, setIsProsumer] = React.useState(true);

  React.useEffect(() => {
    if (existingProject && !hasInitialized) {
      setName(existingProject.name);
      setProjectSlug(existingProject.slug);
      setProjectJson(projectToEditorJson(existingProject));
      setPublishStatus(existingProject.publishStatus ?? 'LIVE');
      setIsProsumer(existingProject.isProsumer ?? true);
      setHasInitialized(true);
    }
  }, [existingProject, hasInitialized]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProjectSlug(e.target.value);
  };

  const handleJsonChange = (value: string) => {
    setProjectJson(value);
    const result = validateJsonBody(value);
    setJsonError(result.error);

    if (result.valid) {
      if (result.data.name) setName(result.data.name);
      if (result.data.slug) setProjectSlug(result.data.slug);
      if (result.data.isProsumer !== undefined)
        setIsProsumer(result.data.isProsumer);
    }
  };

  const jsonResult = validateJsonBody(projectJson);
  const isJsonValid = jsonResult.valid;
  const canSave =
    isJsonValid && name.trim().length > 0 && projectSlug.trim().length > 0;

  const handleSave = async () => {
    if (!jsonResult.valid) return;

    if (!name.trim() || !projectSlug.trim()) {
      toast.error('Name and slug are required');
      return;
    }

    const fullProject: CmsProjectDefinition = {
      ...jsonResult.data,
      name: name.trim(),
      slug: projectSlug.trim(),
      publishStatus,
      isProsumer,
    };

    try {
      if (isEditMode) {
        const { slug: _omitSlug, ...updateData } = fullProject;
        void _omitSlug;
        await updateMutation.mutateAsync(updateData);
        const { data: saved } = await refetch();
        if (saved) {
          setName(saved.name);
          setProjectJson(projectToEditorJson(saved));
        }
        toast.success('Project updated');
      } else {
        await createMutation.mutateAsync(fullProject);
        toast.success('Project created');
        history.push(ROUTES.ADMIN.CMS_PROJECTS.getPath());
      }
    } catch (e) {
      const message = (e as Error).message || 'Something went wrong';
      toast.error(message);
    }
  };

  const handlePreview = () => {
    if (!jsonResult.valid) return;
    if (!name.trim() || !projectSlug.trim()) return;

    const fullProject: CmsProjectDefinition = {
      ...jsonResult.data,
      name: name.trim(),
      slug: projectSlug.trim(),
      publishStatus,
      isProsumer,
    };

    localStorage.setItem(
      PREVIEW_STORAGE_KEY,
      JSON.stringify({ ...fullProject, _createdAt: Date.now() })
    );
    window.open(
      ROUTES.ADMIN.CMS_PROJECT_PREVIEW.getPath({ slug: projectSlug.trim() }),
      '_blank'
    );
  };

  // Clears the pasted definition and the preview copy parked in localStorage.
  // Nothing is persisted until Save, so this only resets the editor.
  const handleClearJson = () => {
    setProjectJson('{}');
    setJsonError(null);
    localStorage.removeItem(PREVIEW_STORAGE_KEY);
    toast.success('Cleared the pasted JSON and its saved preview');
  };

  const isJsonEmpty = ['', '{}'].includes(projectJson.trim());

  const isSaving = createMutation.isLoading || updateMutation.isLoading;
  const [isArchiveWarningOpen, setIsArchiveWarningOpen] = React.useState(false);

  const isArchiving =
    isEditMode &&
    publishStatus === 'ARCHIVED' &&
    (existingProject?.publishStatus ?? 'LIVE') === 'LIVE';

  const handleSaveClick = () => {
    if (isArchiving) {
      setIsArchiveWarningOpen(true);
    } else {
      handleSave();
    }
  };

  const hasChanges =
    isEditMode && existingProject
      ? name !== existingProject.name ||
        projectJson !== projectToEditorJson(existingProject) ||
        publishStatus !== (existingProject.publishStatus ?? 'LIVE') ||
        isProsumer !== (existingProject.isProsumer ?? true)
      : true;

  const editorProps = {
    isEditMode,
    name,
    projectSlug,
    projectJson,
    jsonError,
    isSaving,
    canSave,
    hasChanges,
    publishStatus,
    isProsumer,
    onNameChange: handleNameChange,
    onSlugChange: handleSlugChange,
    onJsonChange: handleJsonChange,
    isArchiveWarningOpen,
    onPublishStatusChange: setPublishStatus,
    onProsumerChange: setIsProsumer,
    onSave: handleSaveClick,
    onConfirmArchive: () => {
      setIsArchiveWarningOpen(false);
      handleSave();
    },
    onCancelArchive: () => setIsArchiveWarningOpen(false),
    onPreview: handlePreview,
    onClearJson: handleClearJson,
    isJsonEmpty,
  };

  if (isEditMode) {
    return (
      <PageWithAsyncData isLoading={isLoading} error={error} retry={refetch}>
        <EditorContent {...editorProps} />
      </PageWithAsyncData>
    );
  }

  return <EditorContent {...editorProps} />;
}

function EditorContent({
  isEditMode,
  name,
  projectSlug,
  projectJson,
  jsonError,
  isSaving,
  canSave,
  hasChanges,
  publishStatus,
  isProsumer,
  isArchiveWarningOpen,
  onNameChange,
  onSlugChange,
  onJsonChange,
  onPublishStatusChange,
  onProsumerChange,
  onSave,
  onConfirmArchive,
  onCancelArchive,
  onPreview,
  onClearJson,
  isJsonEmpty,
}: {
  isEditMode: boolean;
  name: string;
  projectSlug: string;
  projectJson: string;
  jsonError: string | null;
  isSaving: boolean;
  canSave: boolean;
  hasChanges: boolean;
  publishStatus: 'LIVE' | 'ARCHIVED';
  isProsumer: boolean;
  isArchiveWarningOpen: boolean;
  onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSlugChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onJsonChange: (value: string) => void;
  onPublishStatusChange: (status: 'LIVE' | 'ARCHIVED') => void;
  onProsumerChange: (value: boolean) => void;
  onSave: () => void;
  onConfirmArchive: () => void;
  onCancelArchive: () => void;
  onPreview: () => void;
  onClearJson: () => void;
  isJsonEmpty: boolean;
}) {
  return (
    <>
      <Navbar
        breadCrumbs={[
          { label: 'Admin Dashboard', url: ROUTES.ADMIN.HOME.getPath() },
          { label: 'Projects', url: ROUTES.ADMIN.CMS_PROJECTS.getPath() },
          { label: isEditMode ? 'Edit' : 'Create', url: null },
        ]}
      />
      <CMSPageWrapper
        currentPage={CMS_SIDEBAR_PAGES.PROJECTS}
        shouldAddEmptyDiv={false}
        bottomBar={
          <BottomBar>
            <SmallButton
              label="Preview"
              type="secondary"
              onClick={onPreview}
              faIcon="fa-solid fa-eye"
              showIconBeforeLabel
              isDisabled={!canSave}
            />
            <SmallButton
              label={isEditMode ? 'Save Changes' : 'Create Project'}
              onClick={onSave}
              type="primary"
              isLoading={isSaving}
              showIconBeforeLabel
              faIcon={isEditMode ? 'fa-solid fa-save' : 'fa-solid fa-plus'}
              isDisabled={!canSave || !hasChanges}
            />
          </BottomBar>
        }
      >
        <PageTitle>{isEditMode ? 'Edit Project' : 'Create Project'}</PageTitle>
        <EditorPageLayout>
          <FieldRow>
            <FormGroup>
              <Label>Name</Label>
              <Input
                placeholder="e.g. Automated Signal Pipeline"
                value={name}
                onChange={onNameChange}
                style={{ marginTop: 0, paddingBottom: 0 }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Slug</Label>
              <Input
                placeholder="e.g. automated-signal-pipeline"
                value={projectSlug}
                onChange={onSlugChange}
                isDisabled={isEditMode}
                style={{ marginTop: 0, paddingBottom: 0 }}
              />
            </FormGroup>
          </FieldRow>

          <FieldRow>
            <FormGroup style={{ flex: 'none' }}>
              <Label>Status</Label>
              <StatusToggleWrapper>
                <Tabs
                  options={['Archived', 'Live']}
                  selectedOptionIndex={publishStatus === 'LIVE' ? 1 : 0}
                  onClick={(index) =>
                    onPublishStatusChange(index === 1 ? 'LIVE' : 'ARCHIVED')
                  }
                />
              </StatusToggleWrapper>
            </FormGroup>

            <FormGroup style={{ flex: 'none' }}>
              <Label>Prosumer availability</Label>
              <StatusToggleWrapper>
                <Tabs
                  options={['Off', 'On']}
                  selectedOptionIndex={isProsumer ? 1 : 0}
                  onClick={(index) => onProsumerChange(index === 1)}
                />
              </StatusToggleWrapper>
            </FormGroup>
          </FieldRow>

          <FormGroup>
            <LabelRow>
              <Label>Project Definition (JSON)</Label>
              <ClearButton
                type="button"
                onClick={onClearJson}
                disabled={isJsonEmpty}
              >
                Clear
              </ClearButton>
            </LabelRow>
            <EditorWrapper hasError={!!jsonError}>
              <AceEditor
                mode="json"
                theme={ACE_THEME}
                name="project-editor"
                value={projectJson}
                onChange={onJsonChange}
                width="100%"
                height="100%"
                fontSize={14}
                showPrintMargin={false}
                showGutter
                highlightActiveLine
                wrapEnabled
                setOptions={{
                  useWorker: false,
                  showLineNumbers: true,
                  tabSize: 2,
                  wrap: true,
                }}
                style={{ background: 'transparent', flex: 1 }}
              />
            </EditorWrapper>
            {jsonError && <ErrorText>{jsonError}</ErrorText>}
          </FormGroup>
        </EditorPageLayout>
      </CMSPageWrapper>

      {isArchiveWarningOpen && (
        <StickyHeaderModal
          title={
            <ModalTitleContainer>
              <ModalTitleMain>Archive Project</ModalTitleMain>
              <ModalTitleCaption>
                This will hide the project from all users
              </ModalTitleCaption>
            </ModalTitleContainer>
          }
          onClose={onCancelArchive}
        >
          <ModalBody>
            Archiving this project will make it invisible to learners, managers,
            and admins outside of the CMS. If any learners have this project in
            their learning plan, it will no longer be visible there — even if
            they have existing progress on it.
          </ModalBody>
          <ModalActions>
            <Button label="Cancel" type="secondary" onClick={onCancelArchive} />
            <Button
              label="Archive"
              type="primary"
              onClick={onConfirmArchive}
              isLoading={isSaving}
            />
          </ModalActions>
        </StickyHeaderModal>
      )}
    </>
  );
}
